import { useState, useEffect } from 'react';
import { X, ArrowLeft, FileText, Users, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import coconeusesTranslations from '../../translations/coconeusesTranslations';

const CoconeuseBioModal = ({ coconeuse, isOpen, onClose }) => {
  const { language } = useLanguage();
  const t = (obj) => obj?.[language] || obj?.fr || '';

  const data = coconeusesTranslations.coconeuses[coconeuse];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !data) return null;

  // Placeholder photo based on coconeuse name
  const getPlaceholderPhoto = (name) => {
    if (name === 'jeinar') {
      return 'https://customer-assets.emergentagent.com/job_a9e4b3eb-6cf0-46b7-85a6-000614fbaf25/artifacts/w43cpv54_DSC02009.jpg';
    }
    // Default placeholder for others
    return null;
  };

  const photo = data.photo || getPlaceholderPhoto(coconeuse);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#3F2A24]/90 backdrop-blur-sm" />
      
      {/* Modal Content */}
      <div 
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto mx-4 bg-[#F3E5CF] rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-[#3F2A24]/10 hover:bg-[#3F2A24]/20 transition-colors"
          data-testid="coconeuse-modal-close"
        >
          <X className="w-5 h-5 text-[#3F2A24]" />
        </button>

        {/* Back Button */}
        <button
          onClick={onClose}
          className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-2 rounded-full bg-[#3F2A24]/10 hover:bg-[#3F2A24]/20 transition-colors text-[#3F2A24] text-sm"
          data-testid="coconeuse-modal-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t(coconeusesTranslations.backButton)}</span>
        </button>

        {/* Photo Section */}
        <div className="relative w-full aspect-[4/3] bg-[#E8D2B5] overflow-hidden">
          {photo ? (
            <img 
              src={photo} 
              alt={data.name}
              className="w-full h-full object-cover"
              style={{ objectPosition: data.heroPosition || 'center 35%' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-[#C5A059]/30 flex items-center justify-center">
                  <span className="font-serif text-4xl text-[#3F2A24]/40">
                    {data.name.charAt(0)}
                  </span>
                </div>
                <p className="text-sm text-[#3F2A24]/40 italic">
                  {language === 'fr' ? 'Photo à venir' : 'Photo coming soon'}
                </p>
              </div>
            </div>
          )}
          {/* Name overlay on photo */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#3F2A24]/80 to-transparent pt-16 pb-6 px-8">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F3E5CF]">
              {data.name}
            </h2>
            <p className="text-sm sm:text-base text-[#F3E5CF]/80 mt-1">
              {t(data.title)}
            </p>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-8 sm:p-10 space-y-8">
          {/* Role */}
          <div className="text-center">
            <p className="text-xs tracking-[0.3em] uppercase text-[#5C4A3D]/60">
              {t(data.role)}
            </p>
          </div>

          {/* Quote (if exists) */}
          {t(data.quote) && (
            <blockquote className="text-center py-6 border-y border-[#3F2A24]/10">
              <p className="font-serif text-lg sm:text-xl text-[#3F2A24]/80 italic" style={{ textWrap: 'balance' }}>
                "{t(data.quote)}"
              </p>
            </blockquote>
          )}

          {/* Biography */}
          <div className="prose prose-brown max-w-none">
            <p className="text-[#3F2A24]/80 leading-relaxed whitespace-pre-line text-center" style={{ lineHeight: '1.85' }}>
              {t(data.bio)}
            </p>
          </div>

          {/* Story Page Button (for Jeinar) or PDF Button (for others) */}
          {data.pdfButton && (
            <div className="text-center pt-4">
              {data.storyPageUrl ? (
                <Link
                  to={data.storyPageUrl}
                  onClick={onClose}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#C5A059] text-[#3F2A24] rounded-full hover:bg-[#3F2A24] hover:text-[#F3E5CF] transition-colors text-sm font-medium"
                  data-testid="discover-jeinar-story-btn"
                >
                  <BookOpen className="w-4 h-4" />
                  {t(data.pdfButton)}
                </Link>
              ) : data.pdfUrl ? (
                <a
                  href={data.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#C5A059] text-[#3F2A24] rounded-full hover:bg-[#3F2A24] hover:text-[#F3E5CF] transition-colors text-sm font-medium"
                >
                  <FileText className="w-4 h-4" />
                  {t(data.pdfButton)}
                </a>
              ) : (
                <button
                  disabled
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#C5A059]/30 text-[#3F2A24]/50 rounded-full text-sm font-medium cursor-not-allowed"
                >
                  <FileText className="w-4 h-4" />
                  {t(data.pdfButton)}
                </button>
              )}
            </div>
          )}

          {/* Gallery Section (if exists) */}
          {data.galleryTitle && (
            <div className="pt-8 border-t border-[#3F2A24]/10">
              <p className="text-xs tracking-[0.2em] uppercase text-[#5C4A3D]/60 mb-6 text-center">
                {t(data.galleryTitle)}
              </p>
              {data.gallery && data.gallery.length > 0 ? (
                <div className="grid grid-cols-3 gap-3">
                  {data.gallery.map((img, index) => {
                    const imgUrl = typeof img === 'string' ? img : img.url;
                    const imgStyle = typeof img === 'object' && img.objectPosition ? { objectPosition: img.objectPosition } : {};
                    return (
                      <div key={index} className="aspect-square rounded-lg overflow-hidden bg-[#E8D2B5]">
                        <img src={imgUrl} alt={`${data.name} ${index + 1}`} className="w-full h-full object-cover" style={imgStyle} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map((num) => (
                    <div key={num} className="aspect-square rounded-lg overflow-hidden bg-[#E8D2B5] flex items-center justify-center">
                      <span className="text-xs text-[#3F2A24]/30 italic">
                        {data.name} {num}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Expertise (if defined) */}
          {data.expertise && t(data.expertise).length > 0 && t(data.expertise)[0] !== 'À définir' && t(data.expertise)[0] !== 'To be defined' && (
            <div className="pt-6 border-t border-[#3F2A24]/10">
              <p className="text-xs tracking-[0.2em] uppercase text-[#5C4A3D]/60 mb-4 text-center">
                {language === 'fr' ? 'Domaines d\'expertise' : 'Areas of expertise'}
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {t(data.expertise).map((item, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 bg-[#C5A059]/20 rounded-full text-sm text-[#3F2A24]/70"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Back to Coconeuses Link */}
          <div className="pt-8 border-t border-[#3F2A24]/10 text-center">
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 text-[#C5A059] hover:text-[#3F2A24] transition-colors text-sm"
            >
              <Users className="w-4 h-4" />
              <span>{language === 'fr' ? 'Retour aux Coconeuses' : 'Back to the Coconeuses'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoconeuseBioModal;
